import {Helmet} from '@dr.pogodin/react-helmet';
import {BaseFlagProps, openDeleteDialog, showNotification} from 'platform/components';
import {useDateTimeFormatter, useFormatCurrency} from 'platform/locale';
import {match} from 'ts-pattern';

import {always, defaultTo, isNil} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {useGetDocumentContextCountQuery} from '@dms/api/core';
import {useGetUserQuery} from '@dms/api/customer';
import {EntityResourceIds} from '@dms/api/shared';
import {
  useDeleteServiceOrderReturnMutation,
  useGetServiceOrderReturnQuery,
  useGetWarehouseHeaderDynamicActionsQuery,
  usePostPrintoutOnDemandMutation,
  usePutWarehouseHeaderDynamicActionsMutation,
} from '@dms/api/warehouse';
import {featureFlags} from '@dms/feature-flags';
import i18n from '@dms/i18n';
import {testIds, warehouseRoutes} from '@dms/routes';
import {
  createHeaderActions,
  Checklists,
  DetailTemplate,
  DetailTemplateHeader,
  printPDF,
  getUserName,
  handleApiError,
  NavigationItem,
  Section,
  useChecklist,
  useTenant,
  useFileNotification,
} from '@dms/shared';
import {useWarehouseParams} from '@dms/warehouse/shared-components';

import {
  buildArray,
  composePath,
  EMPTY_PLACEHOLDER,
  generateHashFromObjects,
  Nullish,
  parseDate,
  useNavigate,
} from 'shared/web';

import {Documents} from './(sections)/Documents/Documents';
import {Overview} from './(sections)/Overview/Overview';

export function ServiceOrderReturnsDetail() {
  const {serviceOrderReturnId} = useWarehouseParams();
  const fileNotification = useFileNotification({timeoutMs: 30000});

  const navigate = useNavigate();

  const currencyFormatter = useFormatCurrency();

  const formatDate = useDateTimeFormatter();

  const tenant = useTenant();

  const {
    data: serviceOrderReturn,
    isLoading: isServiceOrderReturnLoading,
    isFetching: isServiceOrderReturnFetching,
    isError: hasServiceOrderReturnError,
  } = useGetServiceOrderReturnQuery({serviceOrderReturnId});

  const {data: createdByUser, isLoading: isCreatedByUserLoading} = useGetUserQuery(
    {
      id: serviceOrderReturn?.serviceOrderReturn?.createdBy as string,
    },
    {skip: isNilOrEmpty(serviceOrderReturn?.serviceOrderReturn?.createdBy)}
  );

  const {data: completedByUser, isLoading: isCompletedByUserLoading} = useGetUserQuery(
    {
      id: serviceOrderReturn?.serviceOrderReturn?.updatedBy as string,
    },
    {
      skip:
        serviceOrderReturn?.serviceOrderReturn?.state !== 'COMPLETED' &&
        isNilOrEmpty(serviceOrderReturn?.serviceOrderReturn?.updatedBy),
    }
  );

  const {
    data: serviceOrderReturnActions,
    isLoading: isServiceOrderReturnActionsLoading,
    isError: hasServiceOrderReturnActionsError,
  } = useGetWarehouseHeaderDynamicActionsQuery(
    {id: serviceOrderReturnId, resource: 'service-order-return'},
    {skip: isNilOrEmpty(serviceOrderReturnId)}
  );

  const documentCountQuery = useGetDocumentContextCountQuery(
    {contextTarget: 'service-order-return', contextId: serviceOrderReturnId},
    {skip: !serviceOrderReturnId}
  );

  const [putServiceOrderReturnAction, {isLoading: isPutServiceOrderReturnActionLoading}] =
    usePutWarehouseHeaderDynamicActionsMutation();

  const [postPrintoutOnDemand, {isLoading: isPostPrintoutOnDemandLoading}] =
    usePostPrintoutOnDemandMutation();

  const [deleteServiceOrderReturn] = useDeleteServiceOrderReturnMutation();

  const isLoading =
    isServiceOrderReturnLoading ||
    isCreatedByUserLoading ||
    isCompletedByUserLoading ||
    isServiceOrderReturnActionsLoading;

  const isError = hasServiceOrderReturnError || hasServiceOrderReturnActionsError;

  const serviceOrderReturnTitle = `${i18n.t('page.warehouse.labels.serviceOrderReturn')} ${defaultTo(
    '',
    serviceOrderReturn?.serviceOrderReturn?.number
  )}`;

  const priceWithVat = currencyFormatter(
    defaultTo(0, serviceOrderReturn?.sparePartsTotalPrice?.totalPurchasePrice?.withVat?.amount),
    defaultTo(
      tenant.tenantCurrency,
      serviceOrderReturn?.sparePartsTotalPrice?.totalPurchasePrice?.withVat?.currency
    ),
    2
  );

  const priceWithoutVat = currencyFormatter(
    defaultTo(0, serviceOrderReturn?.sparePartsTotalPrice?.totalPurchasePrice?.withoutVat?.amount),
    defaultTo(
      tenant.tenantCurrency,
      serviceOrderReturn?.sparePartsTotalPrice?.totalPurchasePrice?.withoutVat?.currency
    ),
    2
  );

  const formatToShortDate = (date: string | Nullish) => {
    if (isNil(date)) {
      return EMPTY_PLACEHOLDER;
    }
    return formatDate('dateShort', parseDate(date));
  };

  const createdAt = `${i18n.t('general.labels.dateCreated')}: ${formatToShortDate(
    serviceOrderReturn?.serviceOrderReturn?.createdAt
  )}`;

  const createdBy = `${i18n.t('general.labels.createdBy')}: ${getUserName(createdByUser)}`;

  const completedAt = match(serviceOrderReturn?.serviceOrderReturn?.state)
    .returnType<string | null>()
    .with(
      'COMPLETED',
      always(
        `${i18n.t('general.labels.dateCompleted')}: ${formatToShortDate(
          serviceOrderReturn?.serviceOrderReturn?.updatedAt
        )}`
      )
    )
    .otherwise(always(null));

  const completedBy = match(serviceOrderReturn?.serviceOrderReturn?.state)
    .returnType<string | null>()
    .with(
      'COMPLETED',
      always(`${i18n.t('general.labels.completedBy')}: ${getUserName(completedByUser)}`)
    )
    .otherwise(always(null));

  const headerFlags = match(serviceOrderReturn?.serviceOrderReturn?.state)
    .returnType<Omit<BaseFlagProps, 'size'>[]>()
    .with('COMPLETED', always([{colorScheme: 'green', label: i18n.t('general.labels.completed')}]))
    .otherwise(always([{colorScheme: 'blue', label: i18n.t('general.labels.pending')}]));

  const handleActionClick = (actionKey: string) => {
    if (actionKey === 'prntServiceOrderReturn') {
      return postPrintoutOnDemand({
        body: {
          docOriginId: serviceOrderReturnId,
          originEntityPrintoutType: 'prntServiceOrderReturn',
        },
      })
        .unwrap()
        .then((data) => {
          const fileId = data?.printout?.fileId;
          const pdfUrl = data?.printout?.pdfUrl;

          if (fileId) {
            fileNotification.registerFile(fileId, {
              onSuccess: () => {
                if (pdfUrl) {
                  printPDF(pdfUrl);
                } else {
                  showNotification.error();
                }
              },
            });
          } else {
            showNotification.error();
          }
        })
        .then(() => documentCountQuery.refetch())
        .then(() =>
          showNotification.success(i18n.t('entity.warehouse.labels.documentWasGenerated'))
        )
        .then(() => {
          navigate(
            composePath(warehouseRoutes.serviceOrderReturnsDetailDocuments, {
              params: {id: serviceOrderReturnId},
            })
          );
        })
        .catch(handleApiError);
    }

    match(actionKey)
      .with('delete', () => {
        openDeleteDialog({
          text: i18n.t('entity.warehouse.actions.deleteEntity'),
          onConfirm: () =>
            deleteServiceOrderReturn({body: {serviceOrderReturnId: [serviceOrderReturnId]}})
              .unwrap()
              .then(() => navigate(warehouseRoutes.serviceOrderReturns))
              .catch(handleApiError),
        });
      })
      .otherwise(() => {
        putServiceOrderReturnAction({
          id: serviceOrderReturnId,
          resource: 'service-order-return',
          body: {actionKey, originEntityHeaderType: 'service-order-return'},
        })
          .unwrap()
          .then(() => documentCountQuery.refetch())
          .catch(handleApiError);
      });
  };

  const headerActions = createHeaderActions({
    actions: serviceOrderReturnActions?.actions,
    callback: handleActionClick,
    isLoading: isPutServiceOrderReturnActionLoading || isPostPrintoutOnDemandLoading,
  });

  const header: DetailTemplateHeader = {
    title: serviceOrderReturnTitle,
    isEntityLoading: isServiceOrderReturnFetching,
    icon: 'custom/warehouse',
    parameters: [
      defaultTo('', serviceOrderReturn?.serviceOrderReturn?.number),
      serviceOrderReturn?.serviceOrder?.variant,
      createdAt,
      createdBy,
      completedAt,
      completedBy,
    ],
    recordId: serviceOrderReturn?.serviceOrderReturn?.serviceOrderReturnId,
    resourceId: EntityResourceIds.serviceOrderReturn,
    controls: ['ASSIGNEE'],
    flags: headerFlags,
    actions: headerActions,
    primaryParameter: defaultTo(undefined, priceWithVat),
    secondaryParameter: `${priceWithoutVat} ${i18n.t('general.labels.w/oVat')}`,
  };

  const {badgeSettings, checklistSettings} = useChecklist({
    recordId: serviceOrderReturnId || '',
    resourceId: 'SERVICE_ORDER_RETURNS',
  });

  const navigation = buildArray<NavigationItem>([
    {
      id: 'serviceOrderReturn',
      label: i18n.t('page.warehouse.labels.overview'),
      href: composePath(warehouseRoutes.serviceOrderReturnsDetailOverview, {
        params: {id: serviceOrderReturnId},
      }),
      content: (
        <Overview
          serviceOrderReturn={serviceOrderReturn}
          isServiceOrderReturnLoading={isServiceOrderReturnLoading}
          data-testid={testIds.warehouse.serviceOrderReturnsDetailOverview('section.overview')}
        />
      ),
      'data-testid': testIds.warehouse.serviceOrderReturnsDetailOverview('navigation.overview'),
    },
    {
      id: 'serviceOrderReturnDocuments',
      label: i18n.t('page.warehouse.labels.documents'),
      href: composePath(warehouseRoutes.serviceOrderReturnsDetailDocuments, {
        params: {id: serviceOrderReturnId},
      }),
      content: (
        <Documents
          onDocumentAction={() => documentCountQuery.refetch()}
          data-testid={testIds.warehouse.serviceOrderReturnsDetailOverview('section.documents')}
        />
      ),
      badge: !!documentCountQuery.data?.count
        ? {value: documentCountQuery.data.count, colorScheme: 'neutral'}
        : undefined,
      'data-testid': testIds.warehouse.serviceOrderReturnsDetailOverview('navigation.documents'),
    },
  ]).whenFeatureEnabled(featureFlags.SALE_TASKS_CHECKLIST, {
    id: 'serviceOrderReturnsChecklists',
    label: i18n.t('entity.checklist.labels.title'),
    href: composePath(warehouseRoutes.serviceOrderReturnsDetailChecklists, {
      params: {id: serviceOrderReturnId},
    }),
    content: (
      <Section>
        <Checklists
          {...checklistSettings}
          contextTarget="service-order-return"
          contextId={serviceOrderReturnId}
        />
      </Section>
    ),
    badge: badgeSettings,
    'data-testid': testIds.warehouse.serviceOrderReturnsDetailChecklists('checklists'),
  });

  return (
    <>
      <Helmet title={i18n.t('page.warehouse.labels.serviceOrderReturn')} />
      <DetailTemplate
        key={generateHashFromObjects(serviceOrderReturn)}
        isLoading={isLoading}
        isError={isError}
        isLocked={isPutServiceOrderReturnActionLoading}
        header={header}
        navigation={navigation}
        data-testid={testIds.warehouse.serviceOrderReturnsDetailOverview('page')}
      />
    </>
  );
}
